/**
 * 스크립트 테스트 러너 — 결정 #30(테스트 환경) / #4. core의 모킹 capability API를
 * **실제 스크립트 호스트**(CompositeScriptHost: QuickJS/Lua 샌드박스)에 물려, 스크립트를
 * 프로덕션과 동일하게 실행하되 결정적으로 검증한다.
 *
 * 핵심: 새 실행 경로를 만들지 않는다. 같은 ScriptHost.invoke를 타므로 테스트 통과 =
 * 실제 동작 보장. JS·Lua가 capability 계약을 공유하므로 한 러너로 둘 다 커버한다.
 *
 * 오류 가시화: 호스트는 스크립트 예외를 내부에서 삼키고 원본을 반환한다(대화를 안
 * 죽이려는 프로덕션 동작). 테스트에선 오류를 알아야 하므로, invoke 동안만 console.error를
 * 임시 가로채 수집한다(프로덕션 코드는 건드리지 않음).
 */
import {
  MockCapabilityApi,
  evaluateAssertions,
  type AssertionResult,
  type MockApiOptions,
  type MockApiSnapshot,
  type ScriptAssertion,
  type ScriptCapabilities,
  type ScriptHook,
  type ScriptLanguage,
} from "@roqeet/core";
import { CompositeScriptHost } from "../platform/script/index.js";

export interface ScriptTestInput {
  language: ScriptLanguage;
  source: string;
  /** 실행할 훅(기본 after-response). */
  hook?: ScriptHook;
  /** context.text 초기값(스크립트가 변형할 대상). */
  inputText: string;
  /** 모킹 상태(변수·대화·시드·시각·스토리지). */
  mock?: MockApiOptions;
  /** 기대값(선언적). 주면 통과/실패를 함께 평가. */
  assertion?: ScriptAssertion;
  /** 부여 권한(테스트 시 기본 모두 허용 + 동의). */
  capabilities?: ScriptCapabilities;
}

export interface ScriptTestResult {
  /** 실행 중 스크립트 오류가 없었는지. */
  ok: boolean;
  /** 최종 출력 텍스트(setResult 또는 변형 결과). */
  outputText: string;
  /** 실행 후 모킹 API 스냅샷(변수·로그·스토리지). */
  snapshot: MockApiSnapshot;
  /** 스크립트가 남긴 로그(편의 재노출). */
  logs: unknown[][];
  /** 어서션 평가 결과(assertion을 준 경우). */
  assertion?: AssertionResult;
  /** 실행 중 포착된 오류 메시지(있으면). */
  error?: string;
}

/** 단발 테스트용 호스트(매 실행마다 새로 만들어 상태 누수 차단). */
let sharedHost: CompositeScriptHost | null = null;
function getHost(): CompositeScriptHost {
  if (!sharedHost) sharedHost = new CompositeScriptHost();
  return sharedHost;
}

/**
 * 스크립트를 모킹 환경에서 한 번 실행하고 결과를 돌려준다(결정 #30). 입력 텍스트와
 * 모킹 상태로 invoke한 뒤, 출력·변수 변화·로그를 스냅샷하고 어서션을 평가한다.
 */
export async function runScriptTest(input: ScriptTestInput): Promise<ScriptTestResult> {
  const api = new MockCapabilityApi(input.mock);
  const hook: ScriptHook = input.hook ?? "after-response";
  const host = getHost();

  // 테스트는 권한을 넓게 허용(동의 포함)해 기능을 확인. 실제 설치 시의 권한 게이팅은
  // 별개다 — 여기선 "로직이 의도대로 동작하는가"를 본다.
  const capabilities: ScriptCapabilities = input.capabilities ?? {
    mutateMessages: true,
    variables: true,
  };

  // 호스트가 삼키는 스크립트 오류를 포착하기 위해 invoke 동안만 console.error를 가로챈다.
  // 또한 스크립트 log는 실제 호스트가 api.log가 아니라 console.log로 보내므로(Lua는
  // "[lua]" 접두), console.log도 가로채 실제 찍힌 로그를 수집한다(프로덕션 코드 불변).
  const captured: string[] = [];
  const capturedLogs: unknown[][] = [];
  const originalError = console.error;
  const originalLog = console.log;
  console.error = (...args: unknown[]) => {
    captured.push(args.map((a) => (a instanceof Error ? a.message : String(a))).join(" "));
  };
  console.log = (...args: unknown[]) => {
    // Lua 호스트의 "[lua]" 접두는 제거해 스크립트가 넘긴 인자만 남긴다.
    capturedLogs.push(args[0] === "[lua]" ? args.slice(1) : args);
  };

  let outputText = input.inputText;
  let runtimeError: string | undefined;
  try {
    const loaded = await host.load(
      {
        id: "test-script",
        language: input.language,
        source: input.source,
        hooks: [hook],
        capabilities,
        consentGranted: true,
      },
      api,
    );
    const result = await loaded.invoke(hook, { hook, text: input.inputText });
    outputText = result.text ?? "";
    loaded.dispose();
  } catch (e) {
    // load 단계 등에서의 throw(문법 오류 등)는 여기로 온다.
    runtimeError = e instanceof Error ? e.message : String(e);
  } finally {
    console.error = originalError;
    console.log = originalLog;
  }

  // 호스트가 삼킨 실행 오류가 캡처됐으면 노출.
  if (!runtimeError && captured.length > 0) {
    runtimeError = captured.join("\n");
  }

  const snapshot = api.snapshot();
  // JS 경로는 setResult가 api.setResult로 들어와 snapshot.resultViaApi에 잡힌다.
  if (snapshot.resultViaApi !== undefined) outputText = snapshot.resultViaApi;

  // 실제 호스트가 console.log로 보낸 스크립트 로그를 스냅샷 로그와 합친다.
  const logs = [...snapshot.logs, ...capturedLogs];

  const out: ScriptTestResult = {
    ok: !runtimeError,
    outputText,
    snapshot,
    logs,
    ...(runtimeError ? { error: runtimeError } : {}),
  };
  if (input.assertion) {
    out.assertion = evaluateAssertions(outputText, snapshot, input.assertion);
  }
  return out;
}
